<template>
  <div>
    <div
      v-if="fetching"
      class="
        text-center
        d-flex
        justify-content-center
        align-items-center
        fetching
      "
    >
      <b-spinner variant="secondary"></b-spinner>
    </div>
    <div
      v-if="errorMessage"
      class="
        container
        error-message-box
        justify-content-center
        align-items-center
      "
    >
      <div class="text-center">
        <h5>{{ errorMessage }}</h5>
        <p>
          聊天室代碼：{{ chatroomId }}
        </p>
        <p v-if="$store.state.liffChatroom.asStaff">
          管理帳號代碼：{{ this.senderId }}
        </p>
        <p v-if="!$store.state.liffChatroom.asStaff">
          會員顧客代碼：{{ this.senderId }}
        </p>
        <p v-if="$store.state.liffChatroom.user.outer_code">
          錯誤代碼: {{ $store.state.liffChatroom.user.outer_code }}
        </p>
        <b-button @click="cancel" variant="success" class="mb-1 mt-3">
          回官方帳號
        </b-button>
      </div>
    </div>
    <template v-else>
      <div
        v-if="chatroom"
        class="chatbox flex-fill"
        @scroll="handleScroll"
        ref="chatbox"
      >
        <div class="chatbox-header sticky-top">
          <header
            class="
              d-flex
              justify-content-start
              align-items-center
              border-bottom
            "
          >
            <span v-if="showBackButton" class="chat-back text-left d-sm-none">
              <b-link
                class="btn text-white"
                :to="{ name: 'LiffChatroomIndex' }"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 256 512"
                  fill="white"
                >
                  <path
                    d="M192 448c-8.188 0-16.38-3.125-22.62-9.375l-160-160c-12.5-12.5-12.5-32.75 0-45.25l160-160c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l137.4 137.4c12.5 12.5 12.5 32.75 0 45.25C208.4 444.9 200.2 448 192 448z"
                  />
                </svg>
                返回
              </b-link>
            </span>
            <h1 class="chat-title mb-0">
              <b-avatar
                class="chat-title-avatar mr-2"
                :src="getChatroomAvatar(chatroom)"
              ></b-avatar>
              <span class="chat-title-text">{{ resolveTitle(chatroom) }}</span>
              <span class="chat-title-icons ml-2">
                <span
                  v-if="showDoNotDisturbIcon"
                  :class="[
                    'mdi',
                    doNotDisturbEnabled ? 'mdi-bell-off' : 'mdi-bell',
                    { fading: doNotDisturbIconFading },
                  ]"
                ></span>
                <span
                  v-if="showPinIcon"
                  :class="[
                    'mdi',
                    isPinned ? 'mdi-pin' : 'mdi-pin-off',
                    { fading: pinIconFading },
                  ]"
                ></span>
              </span>
            </h1>
            <span class="chat-header-actions text-right">
              <div
                v-if="isMessageSearching"
                class="chat-header-search d-flex flex-nowrap align-items-center"
              >
                <b-form-input
                  v-model="searchKeyword"
                  type="search"
                  placeholder="搜尋訊息"
                  debounce="300"
                  size="sm"
                  class="chat-header-search-input"
                  @update="handleSearchInput"
                />
                <b-button
                  size="sm"
                  variant="link"
                  class="text-white px-2 flex-shrink-0"
                  @click="exitSearch"
                >
                  ✕
                </b-button>
              </div>
              <b-button
                v-else-if="allowSearch"
                variant="link"
                class="text-white p-2"
                aria-label="搜尋"
                @click="toggleSearch"
              >
                <span class="mdi mdi-magnify"></span>
              </b-button>
              <b-button
                v-if="hasAnyTool"
                variant="link"
                class="text-white p-2"
                :aria-label="showToolbar ? '關閉' : '功能'"
                @click="toggleToolbar"
              >
                <svg
                  v-if="!showToolbar"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  fill="white"
                  width="20"
                  height="20"
                >
                  <path
                    d="M0 96C0 78.33 14.33 64 32 64H416C433.7 64 448 78.33 448 96C448 113.7 433.7 128 416 128H32C14.33 128 0 113.7 0 96zM0 256C0 238.3 14.33 224 32 224H416C433.7 224 448 238.3 448 256C448 273.7 433.7 288 416 288H32C14.33 288 0 273.7 0 256zM416 448H32C14.33 448 0 433.7 0 416C0 398.3 14.33 384 32 384H416C433.7 384 448 398.3 448 416C448 433.7 433.7 448 416 448z"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  width="20"
                  height="20"
                >
                  <path
                    d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  />
                </svg>
              </b-button>
            </span>
          </header>

          <div
            v-if="showToolbar && !showRecallCheckBox"
            class="chat-toolbar px-3 py-2 border-bottom bg-white"
          >
            <div class="chat-toolbar-row d-flex flex-wrap align-items-center">
              <b-button
                v-if="allowDoNotDisturb"
                :variant="doNotDisturbEnabled ? 'secondary' : 'outline-secondary'"
                size="sm"
                class="mr-2 mb-1"
                @click="handleToggleDoNotDisturb"
              >
                {{ doNotDisturbEnabled ? '關閉勿擾' : '勿擾' }}
              </b-button>

              <b-button
                v-if="allowPin"
                :variant="isPinned ? 'warning' : 'outline-warning'"
                size="sm"
                class="mr-2 mb-1"
                @click="handleTogglePin"
              >
                {{ isPinned ? '取消釘選' : '釘選' }}
              </b-button>

              <b-dropdown
                v-if="allowStatus"
                :variant="statusVariant"
                size="sm"
                :text="statusText"
                class="mr-2 mb-1"
              >
                <b-dropdown-item
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  @click="handleSetStatus(opt.value)"
                >
                  {{ opt.text }}
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <div class="chat-toolbar-row d-flex flex-wrap align-items-center">
              <b-button
                v-if="allowRecall"
                size="sm"
                variant="outline-secondary"
                class="mr-2 mb-1"
                @click="enterRecallMode"
              >
                回收訊息
              </b-button>

              <b-button
                v-if="allowFargloryMessageRelay"
                size="sm"
                variant="outline-secondary"
                class="mr-2 mb-1"
              >
                續小檔
              </b-button>
            </div>
          </div>

          <div
            v-if="showRecallCheckBox"
            class="recall-bar d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-white"
          >
            <b-button size="sm" variant="link" @click="cancelRecallMode">取消</b-button>
            <span class="small text-muted">已選擇 {{ selectedRecallMessages.length }} 則</span>
            <b-button
              size="sm"
              variant="danger"
              :disabled="messageRecalling || !selectedRecallMessages.length"
              @click="handleRecallSubmit"
            >
              回收
            </b-button>
          </div>
        </div>

        <div
          class="
            chatbox-inner
            d-flex
            flex-column-reverse
            px-3
            position-relative
          "
        >
          <div class="message-box my-2" v-if="chatroomLoaded && !displayMessages.length">
            <div class="strike small text-muted pb-2">{{ inSearchMode ? '找不到符合的訊息' : '對話開始' }}</div>
          </div>
          <div
            :id="`message-${message.id}`"
            :class="[
              'message-box',
              'my-2',
              message.sender.outer_code ==
                $store.state.liffChatroom.user.outer_code &&
                message.sender.type == ($store.state.liffChatroom.asStaff ? 'staff' : 'customer') &&
                'owner',
            ]"
            :key="message.id"
            v-for="(message, idx) in displayMessages"
          >
            <div
              class="strike small text-muted pb-2"
              v-if="firstMessage && firstMessage.id === message.id && firstLoad"
            >
              對話開始
            </div>
            <div
              class="strike small text-muted pb-2"
              v-else-if="firstNewMessage && firstNewMessage.id === message.id"
            >
              最新訊息
            </div>
            <div
              class="strike small text-muted pb-2"
              v-else-if="firstUnreadMessage && firstUnreadMessage.id === message.id"
            >
              未讀
            </div>
            <div
              class="strike small text-muted pb-2"
              v-if="checkStrike(displayMessages, idx)"
            >
              {{ formatDate(displayMessages[idx].created_at, '今天') }}
            </div>
            <div class="message-wrapper">
              <div class="d-flex align-items-start">
                <div
                  v-if="
                    showRecallCheckBox &&
                    isMessageOwner(message) &&
                    message.content !== '(訊息已收回)' &&
                    message.type !== 'image' &&
                    canRecallByHours(message)
                  "
                  class="mr-2 pt-2 custom-control custom-checkbox"
                >
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    :id="'liff_recall_cb_' + message.id"
                    :value="message.id"
                    :checked="isMessageSelected(message.id, message.type)"
                    :disabled="messageRecalling"
                    @change="handleRecallToggle(message.id, message.type)"
                  />
                  <label
                    class="custom-control-label"
                    :for="'liff_recall_cb_' + message.id"
                  ></label>
                </div>
                <b-avatar
                  v-if="!(
                    message.sender.outer_code ==
                      $store.state.liffChatroom.user.outer_code &&
                      message.sender.type == ($store.state.liffChatroom.asStaff ? 'staff' : 'customer')
                  )"
                  class="mr-2"
                  :src="message.sender.avatar_url"
                ></b-avatar>
                <div>
                  <small
                    class="mb-1"
                    v-if="!(
                      message.sender.outer_code ==
                        $store.state.liffChatroom.user.outer_code &&
                        message.sender.type == ($store.state.liffChatroom.asStaff ? 'staff' : 'customer')
                    )"
                  >
                    {{ message.sender.name }}
                  </small>
                  <template v-if="message.type === 'image' && message.meta.images.length > 0">
                    <div
                      class="message image"
                      :class="{
                        multiple:
                          message.meta.images && message.meta.images.length > 1,
                      }"
                    >
                      <div
                        :class="{
                          expanded: message.meta.images.length & 1 && i === 0,
                        }"
                        :key="i"
                        v-for="(image, i) in message.meta.images"
                      >
                        <img
                          :src="image.previewImageUrl"
                          @click="showImageLightBox(getLightBoxSrcs(message), i)"
                        />
                      </div>
                    </div>
                    <!-- <div><a class="download-all" href="#">下載全部</a></div> -->
                  </template>
                  <div
                    class="message file py-2 px-3"
                    v-else-if="message.type === 'file' && message.content != '(訊息已收回)'"
                  >
                    <a :href="fileSelector(message.meta)" target="_blank">
                      <div class="icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 384 512"
                          fill="currentColor"
                        >
                          <path
                            d="M374.6 310.6l-160 160C208.4 476.9 200.2 480 192 480s-16.38-3.125-22.62-9.375l-160-160c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 370.8V64c0-17.69 14.33-31.1 31.1-31.1S224 46.31 224 64v306.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0S387.1 298.1 374.6 310.6z"
                          />
                        </svg>
                      </div>
                      <div class="info">
                        <p>{{ message.meta.fileName }}</p>
                        <p>{{ filesizeForHuman(message.meta.fileSize) }}</p>
                      </div>
                    </a>
                  </div>
                  <div
                    class="message sticker py-2 px-3"
                    v-else-if="message.type === 'sticker' && message.content != '(訊息已收回)'"
                  >
                    <img :src="message.meta.stickerUrl" />
                  </div>
                  <div
                    class="message py-2 px-3"
                    :class="message.draft ? 'draft-message' : 'message'"
                    v-html="manipulateMessage(message.content)"
                    v-else
                  />
                </div>
              </div>
              <div class="small text-muted d-flex align-items-end justify-content-end mt-1">
                <ul class="list-unstyled read-list">
                  <template v-for="sender in senders">
                    <li
                      :title="sender.name"
                      :key="sender.id"
                      v-if="senderIsRead(sender, message)"
                    >
                      <b-avatar size="1rem" :src="sender.avatar_url"></b-avatar>
                    </li>
                  </template>
                </ul>
                <time v-if="!message.draft">{{ formatHourMinutes(message.created_at) }}</time>
              </div>
            </div>
          </div>
          <div class="typing-list" v-if="typingSenders.length">
            <div
              class="message-box my-2"
              :key="sender.id"
              v-for="sender in typingSenders"
            >
              <div class="message-wrapper">
                <div class="d-flex align-items-start">
                  <b-avatar class="mr-2" :src="sender.avatar_url" />
                  <div>
                    <small>{{ sender.name }}</small>
                    <div class="message py-2 px-3 mt-1">
                      <Typing />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="text-center mt-2 mb-auto loading-spinner small" v-if="chatroomLoaded">
            <b-spinner variant="secondary" small v-if="loading"/>
            <button class="btn btn-secondary btn-small" @click.prevent="loadMore" v-else-if="displayNextCursor">載入之前的對話</button>
            <span v-else-if="!firstLoad">已無對話紀錄</span>
          </div>
        </div>

        <div class="typebox py-2 px-3 bg-white border-top">
          <div
            class="position-absolute unread d-flex justify-content-center px-3"
            v-if="showIndicator || showScrollToUnreadMessage"
          >
            <b-button
              v-if="showIndicator && unread"
              variant="dark"
              size="sm"
              pill
              @click="scrollToBottom"
            >
              <div class="last-message-wrapper">
                {{ latestMessage.sender.name }}：{{ latestMessage.content }}
              </div>
            </b-button>

            <b-button
              v-else-if="showIndicator && !bottom"
              variant="dark"
              size="sm"
              class="px-2"
              pill
              @click="scrollToBottom"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                height="16"
                fill="white"
              >
                <!--! Font Awesome Pro 6.0.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. -->
                <path
                  d="M224 416c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L224 338.8l169.4-169.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-192 192C240.4 412.9 232.2 416 224 416z"
                />
              </svg>
            </b-button>

            <b-button
              v-else-if="showScrollToUnreadMessage"
              variant="dark"
              size="sm"
              class="show-scroll-to-unread-message-btn px-2"
              pill
              @click="scrollToUnreadMessage"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                height="16"
                fill="white"
              >
                <path
                  d="M224 416c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L224 338.8l169.4-169.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-192 192C240.4 412.9 232.2 416 224 416z"
                />
              </svg>
            </b-button>
          </div>
          <input
            class="sr-only"
            type="file"
            ref="file"
            @change="uploadFile"
            :multiple="canMultiUpload"
          />
          <div @submit.prevent class="d-flex">
            <button
              v-if="canMultiUpload"
              class="btn px-2"
              type="button"
              :disabled="sending"
              @click.prevent="selectFile"
            >
              <img
                src="@/assets/images/liff/chatbox/paperclip-2.png"
                width="25"
                height="25"
              />
            </button>
            <b-dropdown
              v-if="!canMultiUpload"
              dropup
              class="p-0 btn btn-link file"
              variant=" px-3"
              no-caret
              :disabled="sending"
              @click.prevent
            >
              <template #button-content>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  style="width: 16px; height: 16px"
                >
                  <path
                    d="M396.2 83.8c-24.4-24.4-64-24.4-88.4 0l-184 184c-42.1 42.1-42.1 110.3 0 152.4s110.3 42.1 152.4 0l152-152c10.9-10.9 28.7-10.9 39.6 0s10.9 28.7 0 39.6l-152 152c-64 64-167.6 64-231.6 0s-64-167.6 0-231.6l184-184c46.3-46.3 121.3-46.3 167.6 0s46.3 121.3 0 167.6l-176 176c-28.6 28.6-75 28.6-103.6 0s-28.6-75 0-103.6l144-144c10.9-10.9 28.7-10.9 39.6 0s10.9 28.7 0 39.6l-144 144c-6.7 6.7-6.7 17.7 0 24.4s17.7 6.7 24.4 0l176-176c24.4-24.4 24.4-64 0-88.4z"
                  />
                </svg>
              </template>
              <b-dropdown-item
                @click="selectFile"
                link-class="d-flex justify-content-between align-items-center"
                ><span>單檔</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 384 512"
                  style="width: 16px; height: 16px"
                >
                  <path
                    d="M0 64C0 28.65 28.65 0 64 0H229.5C246.5 0 262.7 6.743 274.7 18.75L365.3 109.3C377.3 121.3 384 137.5 384 154.5V448C384 483.3 355.3 512 320 512H64C28.65 512 0 483.3 0 448V64zM336 448V160H256C238.3 160 224 145.7 224 128V48H64C55.16 48 48 55.16 48 64V448C48 456.8 55.16 464 64 464H320C328.8 464 336 456.8 336 448z"
                  />
                </svg>
              </b-dropdown-item>
              <b-dropdown-item
                @click="openMultipleUploadPage"
                link-class="d-flex justify-content-between align-items-center"
                ><span>多檔</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  style="width: 16px; height: 16px"
                >
                  <path
                    d="M502.6 70.63l-61.25-61.25C435.4 3.371 427.2 0 418.7 0H255.1c-35.35 0-64 28.66-64 64l.0195 256C192 355.4 220.7 384 256 384h192c35.2 0 64-28.8 64-64V93.25C512 84.77 508.6 76.63 502.6 70.63zM464 320c0 8.836-7.164 16-16 16H255.1c-8.838 0-16-7.164-16-16L239.1 64.13c0-8.836 7.164-16 16-16h128L384 96c0 17.67 14.33 32 32 32h47.1V320zM272 448c0 8.836-7.164 16-16 16H63.1c-8.838 0-16-7.164-16-16L47.98 192.1c0-8.836 7.164-16 16-16H160V128H63.99c-35.35 0-64 28.65-64 64l.0098 256C.002 483.3 28.66 512 64 512h192c35.2 0 64-28.8 64-64v-32h-47.1L272 448z"
                  />
                </svg>
              </b-dropdown-item>
            </b-dropdown>

            <div class="textbox flex-fill">
              <b-form-textarea
                class="message-box pl-3"
                :style="`--lines: ${messageLines}`"
                v-model="message"
                placeholder="輸入留言"
                ref="input"
                rows="1"
                :autofocus="false"
              ></b-form-textarea>

              <button
                class="btn send"
                type="button"
                :disabled="sending"
                @click="send"
              >
                <svg
                  width="21"
                  height="18"
                  viewBox="0 0 21 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.17 6.22991L5.17001 0.449907C4.64029 0.230693 4.05884 0.167851 3.49451 0.26882C2.93018 0.369788 2.4066 0.630339 1.98575 1.01963C1.5649 1.40891 1.2644 1.91064 1.11983 2.4654C0.975262 3.02016 0.992675 3.60473 1.17001 4.14991L2.71001 8.99991L1.13001 13.8499C0.947881 14.3973 0.927422 14.9855 1.07108 15.5442C1.21475 16.1029 1.51644 16.6083 1.94001 16.9999C2.48465 17.505 3.19729 17.79 3.94001 17.7999C4.33474 17.7996 4.72554 17.7215 5.09001 17.5699L19.14 11.7899C19.6872 11.5623 20.1547 11.1778 20.4836 10.6848C20.8125 10.1919 20.988 9.61252 20.988 9.01991C20.988 8.42729 20.8125 7.84795 20.4836 7.35497C20.1547 6.862 19.6872 6.47747 19.14 6.24991L19.17 6.22991ZM4.36001 15.6999C4.18361 15.7727 3.99007 15.7934 3.80225 15.7598C3.61442 15.7261 3.44015 15.6394 3.30001 15.5099C3.168 15.3838 3.07304 15.2239 3.02545 15.0476C2.97785 14.8713 2.97943 14.6854 3.03001 14.5099L4.49001 9.99991H18.22L4.36001 15.6999ZM4.49001 7.99991L3.00001 3.52991C2.94943 3.35446 2.94785 3.16851 2.99545 2.99222C3.04304 2.81594 3.138 2.65606 3.27001 2.52991C3.36412 2.43118 3.47742 2.35275 3.60296 2.29944C3.7285 2.24613 3.86362 2.21906 4.00001 2.21991C4.13406 2.22016 4.26669 2.24737 4.39001 2.29991L18.22 7.99991H4.49001Z"
                    fill="#09101D"
                  />
                </svg>
              </button>
            </div>

            <!-- <button
            style="left:0"
              type="button"
              @click="
                () => {

                  handleMessageReceived({
                    message: {
                      content: 'testSend',
                      created_at: new Date(),
                    },
                    customer: chatroom.customers[1],
                    state: '',
                  });
                }
              "
            >
              Test Send
            </button> -->

            <!-- <button
              type="button"
              @click="
                () => {
                  handleMessageReceived({
                    message: {
                      content: 'testReceived',
                      created_at: new Date(),
                    },
                    customer: chatroom.customers[0],
                    state: '',
                  });
                }
              "
            >
              Test Received
            </button> -->
          </div>
        </div>
      </div>
    </template>
    <ImageLightBox ref="lightbox" />
  </div>
</template>

<script>
import format from "date-fns/format";
import { mapGetters, mapActions } from "vuex";
import { v4 as uuidv4 } from "uuid";
import echo from "@/utils/liff/laravelEcho";
import withFetcher from "@/mixins/liff/withFetcher";
import { setTitle } from "@/store/modules/liff/helpers";
import {
  getChatroomAvatar,
  getChatroomTitle,
  formatDate,
  formatHourMinutes,
} from "@/utils/chatroomHelper.js";
import { resolveChatroomTitle } from "@/utils/liffChatroomTitle";
import chatoomMixin from "@/mixins/chatroom";
import _ from "lodash";
import liff from "@line/liff";
import { handleCustomUpload } from "@/utils/ImageHelper"

export default {
  props: ["chatroomId"],
  mixins: [withFetcher, chatoomMixin],
  data: () => ({
    busy: false,
    loading: false,
    sending: false,
    unread: false,
    bottom: true,
    messages: [],
    message: "",
    nextCursor: null,
    stateBag: [],
    scrollHeight: 0,
    chatroom: null,
    showBackButton: false,
    errorMessage: '',
    allowPdfreaderInLiff: false,
    canMultiUpload: true,
    chatroomLoaded: false,
    liffChatroomConfig: null,
    showToolbar: false,
    isMessageSearching: false,
    searchKeyword: '',
    searchResults: [],
    searchNextCursor: null,
    searching: false,
    showRecallCheckBox: false,
    selectedRecallMessages: [],
    messageRecalling: false,
    canRecallHours: 0,
    doNotDisturbEnabled: false,
    doNotDisturbUntil: null,
    doNotDisturbIconFading: false,
    showDoNotDisturbInactiveIcon: false,
    pinIconFading: false,
    showPinInactiveIcon: false,
    iconFadeTimers: {},
    statusOptions: [
      { text: '待處理', value: 'pending', variant: 'danger' },
      { text: '處理中', value: 'doing', variant: 'warning' },
      { text: '處理完畢', value: 'done', variant: 'primary' },
      { text: '未設定', value: null, variant: 'secondary' },
    ],
  }),
  computed: {
    ...mapGetters({
      user: "liffChatroom/user",
      liffId: "liffChatroom/liffId",
    }),
    senderId() {
      const user = this.$store.state.liffChatroom.user;
      console.log(user)
      if (user.staff) {
        return user.staff.id;
      }

      return user.id;
    },
    senders() {
      return this.chatroom?.senders || [];
    },
    showIndicator() {
      const chatbox = this.$refs.chatbox || {};

      if (!chatbox) {
        return false;
      }

      return (
        chatbox.scrollHeight - (chatbox.offsetHeight + this.scrollTop) >= 100
      );
    },
    showScrollToUnreadMessage() {
      return !this.showIndicator && this.firstUnreadMessage
    },
    asStaff() {
      return this.$store.state.liffChatroom.asStaff;
    },
    allowPin() {
      return this.flagFromConfig('allow_pin');
    },
    allowRecall() {
      return this.flagFromConfig('allow_recall_message');
    },
    allowStatus() {
      return this.flagFromConfig('allow_set_status');
    },
    allowSearch() {
      return this.asStaff;
    },
    allowDoNotDisturb() {
      return this.flagFromConfig('allow_do_not_disturb');
    },
    allowFargloryMessageRelay() {
      return this.flagFromConfig('allow_farglory_message_relay');
    },
    doNotDisturbOptions() {
      const options = this.liffChatroomConfig && this.liffChatroomConfig.do_not_disturb_options;
      const supportedUnits = ['hour', 'minute'];

      if (Array.isArray(options) && options.length) {
        return options.filter((option) => supportedUnits.includes(option.unit));
      }

      return [];
    },
    hasAnyTool() {
      return this.allowDoNotDisturb
        || this.allowPin
        || this.allowRecall
        || this.allowStatus
        || this.allowFargloryMessageRelay;
    },
    isPinned() {
      const ext = this.chatroom && this.chatroom.extensions && this.chatroom.extensions[0];
      return !!(ext && ext.pinned_at);
    },
    showDoNotDisturbIcon() {
      return this.doNotDisturbEnabled || this.showDoNotDisturbInactiveIcon;
    },
    showPinIcon() {
      return this.isPinned || this.showPinInactiveIcon;
    },
    currentStatusOption() {
      const value = this.chatroom ? this.chatroom.status : null;
      return this.statusOptions.find((o) => o.value === value) || this.statusOptions.find((o) => o.value === null);
    },
    statusText() {
      return this.currentStatusOption.text;
    },
    statusVariant() {
      return this.currentStatusOption.variant;
    },
    // 搜尋模式下顯示搜尋結果，否則顯示原本訊息列表，避免污染主要 state
    inSearchMode() {
      return this.isMessageSearching && !!this.searchKeyword;
    },
    displayMessages() {
      return this.inSearchMode ? this.searchResults : this.messages;
    },
    displayNextCursor() {
      return this.inSearchMode ? this.searchNextCursor : this.nextCursor;
    },
  },
  async created() {
    await this.getConfigSetting();
  },
  mounted() {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    window.addEventListener("resize", function () {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    });
  },
  beforeDestroy() {
    Object.values(this.iconFadeTimers).forEach(clearTimeout);
  },
  methods: {
    format,
    formatHourMinutes,
    formatDate,
    getChatroomTitle,
    getChatroomAvatar,
    resolveTitle(chatroom) {
      const asStaff = this.$store.state.liffChatroom.asStaff;
      const rendered = resolveChatroomTitle(
        chatroom,
        asStaff,
        this.liffChatroomConfig,
        'chatroom'
      );
      return rendered || getChatroomTitle(chatroom);
    },
    ...mapActions({
      getMessages: "liffChatroom/getMessages",
      sendMessage: "liffChatroom/sendMessage",
      getChatrooms: "liffChatroom/getChatrooms",
      getChatroom: "liffChatroom/getChatroom",
      markAsRead: 'liffChatroom/markAsRead',
      upload: 'liffChatroom/upload',
      markAsUploaded: 'liffChatroom/markAsUploaded',
      getConfig: 'liffChatroom/getConfig',
      generateMultipleUploadToken: "liffChatroom/generateMultipleUploadToken",
      getDoNotDisturb: "liffChatroom/getDoNotDisturb",
      updateDoNotDisturb: "liffChatroom/updateDoNotDisturb",
    }),
    async fetch() {
      try {
        const chatroom = await this.getChatroom({ chatroomId: this.chatroomId })
        this.chatroom = chatroom;

        try {
          await this.$store.dispatch('liffModule/fetchModule', 'liff_chatroom');
          const module = this.$store.getters['liffModule/getModule']('liff_chatroom');
          this.liffChatroomConfig = (module && module.module_config) || null;
        } catch (e) {
          this.liffChatroomConfig = null;
        }

        await this.fetchDoNotDisturbStatus();

        const { messages, nextCursor } = await this.load();
        this.messages = messages;

        this.nextCursor = nextCursor;

        setTitle(this.$store.state.liffChatroom.liffInfo.liff_og_title);

        if (this.$route.query.back) {
          this.showBackButton = true;
        }

        this.chatroomLoaded = true
      } catch (error) {
        // 處理 403 權限錯誤
        if (error.response && error.response.status === 403) {
          const errorData = error.response.data;
          const errorMessage = errorData.message || "您無權限進入此聊天室";
          const allowJoin = errorData.allow_join || false;
          const chatroomData = errorData.chatroom;

          // 如果 allow_join=true，轉到 goToChat 處理加入流程
          if (allowJoin && chatroomData) {
            this.$router.push({
              name: "LiffChatroomGoToChat",
              params: { orgCode: this.$route.params.orgCode },
              query: {
                source_id: chatroomData.source_id,
                source_type: chatroomData.source_type,
                chatroom_id: this.chatroomId
              },
            });
            return;
          }

          this.clearData(errorMessage);
        } else {
          // 其他錯誤
          this.clearData("載入聊天室時發生錯誤");
        }
      }
    },
    fetched() {
      this.setSenderLastReadAt();
      this.setLastLatestMessageSentAt();
      this.connectToChannel();
      this.listenForChannelEvents();
      this.focusInput();
      if (!this.firstUnreadMessage) this.scrollToBottom();
      this.scrollToUnreadMessage();
      this.deboundedRead(this);
    },
    async load(cursor = null, count = 10, keyword = undefined) {
      if (this.busy) return;

      this.busy = true;

      const {
        messages,
        meta: { nextCursor },
      } = await this.getMessages({
        chatroomId: this.chatroomId,
        cursor,
        count,
        keyword,
      });

      this.busy = false;

      return { messages, nextCursor };
    },
    async loadMore() {
      if (this.busy) return;

      if (this.inSearchMode) {
        if (!this.searchNextCursor) return;

        this.loading = true;
        const { messages, nextCursor } = await this.load(this.searchNextCursor, 10, this.searchKeyword);
        this.searchResults = [...this.searchResults, ...messages];
        this.searchNextCursor = nextCursor;
        this.loading = false;
        return;
      }

      if (!this.nextCursor) return;

      this.loading = true;
      this.firstLoad = false;

      const { messages, nextCursor } = await this.load(this.nextCursor);

      this.messages = [...this.messages, ...messages];
      this.nextCursor = nextCursor;

      this.loading = false;
    },
    async send() {
      if (this.sending || this.message == "") return;

      this.sending = true;

      const state = uuidv4();
      const message = this.message;

      this.stateBag.push(state);

      this.message = "";
      this.messages.unshift(this.buildMessageWithState({ state, message }));

      await this.sendMessage({ chatroomId: this.chatroomId, message, state });

      this.deboundedRead(this);

      this.sending = false;
    },
    clearData(message) {
      this.chatroom = null;
      this.messages = null;
      this.errorMessage = message;
    },
    handleMessageReceived(event) {
      let { message, sender, state } = event;

      const index = this.stateBag.indexOf(state);

      if (index !== -1) {
        const compensation = this.messages.find((message) => {
          return message.state === state;
        });

        compensation.id = message.id;
        compensation.content = message.content;
        compensation.type = message.type;
        compensation.meta = message.meta;
        compensation.sender = sender;
        compensation.draft = false

        this.stateBag.splice(index, 1);

        return;
      } else {
        const compensation = this.messages.find((message) => {
          return message.state === state;
        });
        if (compensation) {
          compensation.draft = false
        }
      }

      this.messages.unshift({ sender, ...message });

      this.$nextTick(() => {
        if (this.showIndicator) {
          return;
        }

        this.scrollToBottom();
      });

      // if (!this.bottom) {
      //   return (this.unread = true);
      // }

      this.deboundedRead(this);
    },
    connectToChannel() {
      this.channel = echo.private(this.channelName);
    },
    leaveChannel() {
      echo.leave(this.channelName);
    },
    handleScroll(e) {
      let chatbox = e.target;
      const scrollTop = Math.floor(chatbox.scrollTop);

      this.scrollTop = scrollTop;

      if (this.displayNextCursor && scrollTop <= 0) {
        this.loadMore();
      } else if (
        chatbox.offsetHeight + Math.abs(scrollTop) >=
        chatbox.scrollHeight
      ) {
        this.unread = false;
        this.bottom = true;
        this.deboundedRead(this);
      } else {
        this.bottom = false;
      }
    },
    focusInput() {
      if (this.chatroom) {
        this.$nextTick(() => this.$refs.input.focus());
      }
    },
    scrollToBottom() {
      const chatbox = this.$refs.chatbox;

      if (chatbox) {
        chatbox.scrollTo(0, Number.MAX_SAFE_INTEGER);
      }
    },
    checkStrike(messages, idx) {
      function isSameDay(date1, date2) {
        return (
          date1.getFullYear() === date2.getFullYear() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getDate() === date2.getDate()
        );
      }

      const isLastMessage = idx === messages.length - 1;

      if (isLastMessage) {
        return true;
      }

      return !isSameDay(
        new Date(messages[idx].created_at),
        new Date(messages[idx + 1].created_at)
      );
    },
    getTarget(senders) {
      var _this = this;
      return senders.find((x) => {
        return x.outer_code != _this.$store.state.liffChatroom.user.outer_code;
      });
    },

    getAvatar(sender) {
      if (sender.avatar_url !== undefined && sender.avatar_url)
        return sender.avatar_url;
      else return null;
    },
    deboundedRead: _.debounce(
      (context) => context.markAsRead({ chatroomId: context.chatroomId }),
      250,
      { maxWait: 1000 }
    ),
    selectFile() {
      const fileInput = this.$refs.file;

      fileInput.value = null;
      fileInput.click();
    },
    async uploadFile(e) {
      if (this.sending) {
        return;
      }

      const files = [...e.target.files]

      this.sending = true;

      let { converted, failed } = await handleCustomUpload(
        files,
        this.chatroom.module_config?.upload_image ?? {},
        this.chatroom.module_config?.upload_file ?? {}
      )

      if (failed.length > 0) {
        let message = '';

        for (let i = 0; i < failed.length; i++) {
          let actualSize = (failed[i].actual_size / (1024 * 1024)).toFixed(2)
          message += `檔案: ${failed[i].name} 上傳失敗，原因：${failed[i].reason}，檔案大小： ${actualSize}<br>`
        }

        this.$swal("失敗", message + `請重新選擇其他圖片。`, "warning");
        this.sending = false;
        return;
      }

      try {
        const state = uuidv4();

        this.stateBag.push(state);
        this.messages.unshift(
          this.buildMessageWithState({ state, message: "上傳中..." })
        );

        const filesInfo = converted.map(this.getFileinfo);
        const { files: uploadInfo } = await this.upload({
          chatroomId: this.chatroomId,
          files: filesInfo,
        });
        const requests = uploadInfo.map(({ fileId, uploadURL }, index) => {
          return this.createUploader({
            id: fileId,
            url: uploadURL,
            file: converted[index],
          }).start();
        });

        await Promise.all(requests);
        await this.markAsUploaded({
          chatroomId: this.chatroomId,
          state,
          files: uploadInfo,
        });
      } catch (e) {
        console.error("Upload error:", e);
      } finally {
        this.sending = false;
      }
    },
    buildMessageWithState({ state, message }) {
      const asStaff = this.$store.state.liffChatroom.asStaff;
      const user = this.$store.state.liffChatroom.user;

      // 根據 asStaff 建立正確的 sender
      let sender;
      if (asStaff && user.staff) {
        sender = {
          ...user.staff,
          outer_code: user.staff.line_id || user.outer_code,
          type: 'staff'
        };
      } else {
        sender = {
          ...this.user.customer(),
          type: 'customer'
        };
      }

      return {
        state,
        content: message,
        created_at: new Date().toISOString(),
        sender,
        draft: true
      };
    },
    decorateURL(url) {
      let myURL = new URL(url);
      let searchParams = myURL.searchParams;
      searchParams.set('openExternalBrowser', '1');
      myURL.search = searchParams.toString();

      return myURL.toString();
    },
    cancel() {
      liff.closeWindow();
    },
    fileSelector(meta) {
      if (this.isPdfFile(meta.fileUrl) && this.allowPdfreaderInLiff) {
        return this.pdfViewerLink(meta);
      }
      return `${meta.fileUrl}?openExternalBrowser=1`;
    },
    async getConfigSetting() {
      const config = await this.getConfig();

      this.allowPdfreaderInLiff = config.allow_pdfreader_in_liff ?? false;
      this.canRecallHours = (config.recall_message && config.recall_message.can_recall_time_by_hour) || 0;

      if (liff.getOS() === "android") {
        this.canMultiUpload = false;
      }
    },

    flagFromConfig(key) {
      const senderConfigKey = this.asStaff ? 'sender_type_staff' : 'sender_type_customer';
      const cfg = this.liffChatroomConfig
        && this.liffChatroomConfig[senderConfigKey]
        && this.liffChatroomConfig[senderConfigKey].chatroom;
      return !!(cfg && cfg[key] === true);
    },

    async fetchDoNotDisturbStatus() {
      try {
        const status = await this.getDoNotDisturb({ chatroomId: this.chatroomId });
        this.setDoNotDisturbStatus(status);
      } catch (e) {
        console.error('fetch do-not-disturb error:', e);
      }
    },

    setDoNotDisturbStatus(status) {
      this.doNotDisturbUntil = status ? status.not_disturb_until : null;
      this.doNotDisturbEnabled = !!(status && status.is_do_not_disturb);

      if (this.doNotDisturbEnabled) {
        this.showDoNotDisturbInactiveIcon = false;
        this.doNotDisturbIconFading = false;
      }
    },

    addDoNotDisturbDuration(baseDate, option) {
      const date = new Date(baseDate.getTime());
      const duration = Number(option.duration) || 0;

      if (option.unit === 'hour') {
        date.setHours(date.getHours() + duration);
      } else {
        date.setMinutes(date.getMinutes() + duration);
      }

      date.setSeconds(59, 0);

      return date;
    },

    formatDoNotDisturbDisplay(value) {
      if (!value) return '';
      return format(new Date(value), 'yyyy-MM-dd HH:mm');
    },

    formatDoNotDisturbPayload(value) {
      return format(new Date(value), 'yyyy-MM-dd HH:mm:ss');
    },

    buildDoNotDisturbChoices() {
      const baseDate = new Date();

      return this.doNotDisturbOptions.map((option) => {
        const notDisturbUntil = this.addDoNotDisturbDuration(baseDate, option);

        return {
          ...option,
          notDisturbUntil,
          notDisturbUntilPayload: this.formatDoNotDisturbPayload(notDisturbUntil),
          notDisturbUntilText: this.formatDoNotDisturbDisplay(notDisturbUntil),
        };
      });
    },

    isMessageOwner(message) {
      const userOuterCode = this.$store.state.liffChatroom.user.outer_code;
      const expectedType = this.asStaff ? 'staff' : 'customer';
      return message.sender
        && message.sender.outer_code === userOuterCode
        && message.sender.type === expectedType;
    },

    canRecallByHours(message) {
      if (!this.canRecallHours) return false;
      const diffHour = (new Date() - new Date(message.created_at)) / 3600000;
      return diffHour < this.canRecallHours;
    },

    isMessageSelected(id, type) {
      return this.selectedRecallMessages.findIndex((rm) => rm.id === id && rm.type === type) !== -1;
    },

    getSelectedRecallSender() {
      const selected = this.selectedRecallMessages[0];
      if (!selected) return null;

      const message = this.displayMessages.find((message) => {
        return message.id === selected.id && message.type === selected.type;
      });

      return message && message.sender;
    },

    toggleToolbar() {
      this.showToolbar = !this.showToolbar;
    },

    toggleSearch() {
      if (this.isMessageSearching) {
        // 由搜尋模式切回正常：清除搜尋結果，原本訊息保持不變
        this.exitSearch();
      } else {
        this.isMessageSearching = true;
        this.showToolbar = false;
      }
    },

    exitSearch() {
      this.isMessageSearching = false;
      this.searchKeyword = '';
      this.searchResults = [];
      this.searchNextCursor = null;
    },

    async handleSearchInput(keyword) {
      // 空字串視為退出搜尋結果（顯示原本訊息），但 input 仍保留在搜尋模式
      if (!keyword) {
        this.searchResults = [];
        this.searchNextCursor = null;
        return;
      }
      try {
        this.searching = true;
        this.loading = true;
        const { messages, meta } = await this.getMessages({
          chatroomId: this.chatroomId,
          keyword,
        });
        // keyword 在 await 期間可能改變，避免覆蓋掉最新的搜尋結果
        if (keyword !== this.searchKeyword) return;
        this.searchResults = messages;
        this.searchNextCursor = (meta && meta.nextCursor) || null;
      } catch (e) {
        console.error('search error:', e);
      } finally {
        this.searching = false;
        this.loading = false;
      }
    },

    async handleTogglePin() {
      const willPin = !this.isPinned;
      try {
        const action = willPin ? 'liffChatroom/pinChatroom' : 'liffChatroom/unpinChatroom';
        await this.$store.dispatch(action, { chatroomId: this.chatroomId });
        // API 回傳格式未統一 wrap data，直接以 local state 反映新狀態
        if (!this.chatroom.extensions) {
          this.$set(this.chatroom, 'extensions', []);
        }
        const current = this.chatroom.extensions[0] || {};
        this.$set(this.chatroom.extensions, 0, {
          ...current,
          pinned_at: willPin ? new Date().toISOString() : null,
        });
        this.updatePinIconAfterToggle(willPin);
      } catch (e) {
        console.error('toggle pin error:', e);
      }
    },

    async handleToggleDoNotDisturb() {
      if (this.doNotDisturbEnabled) {
        await this.handleDisableDoNotDisturb();
        return;
      }

      await this.handleEnableDoNotDisturb();
    },

    async handleEnableDoNotDisturb() {
      const selectedOption = await this.showEnableDoNotDisturbSwal();

      if (!selectedOption) return;

      try {
        const status = await this.updateDoNotDisturb({
          chatroomId: this.chatroomId,
          action: 'enable',
          notDisturbUntil: selectedOption.notDisturbUntilPayload,
        });

        this.setDoNotDisturbStatus(status);
        this.updateDoNotDisturbIconAfterToggle(true);
      } catch (e) {
        console.error('enable do-not-disturb error:', e);
        this.$swal('設定勿擾失敗', e.message || '', 'error');
      }
    },

    async handleDisableDoNotDisturb() {
      const confirmed = await this.showDisableDoNotDisturbSwal();

      if (!confirmed) return;

      try {
        const status = await this.updateDoNotDisturb({
          chatroomId: this.chatroomId,
          action: 'disable',
        });

        this.setDoNotDisturbStatus(status);
        this.updateDoNotDisturbIconAfterToggle(false);
      } catch (e) {
        console.error('disable do-not-disturb error:', e);
        this.$swal('關閉勿擾失敗', e.message || '', 'error');
      }
    },

    async showEnableDoNotDisturbSwal() {
      const choices = this.buildDoNotDisturbChoices();

      if (!choices.length) {
        await this.$swal('設定勿擾失敗', '尚未設定勿擾選項', 'warning');
        return null;
      }

      let selectedIndex = 0;
      const optionsHtml = choices.map((choice, index) => `
        <button
          type="button"
          class="chat-dnd-option ${index === selectedIndex ? 'selected' : ''}"
          data-index="${index}"
        >
          <span class="mdi mdi-check chat-dnd-option-check"></span>
          <span>${choice.text}</span>
        </button>
      `).join('');
      const result = await this.$swal.fire({
        title: '設定勿擾',
        html: `
          <div class="chat-dnd-swal">
            <div class="chat-dnd-options">${optionsHtml}</div>
            <div class="chat-dnd-until">
              <span data-role="dnd-until">${choices[0]?.notDisturbUntilText || ''}</span> 前<br>
              不會收到通知
            </div>
          </div>
        `,
        showCancelButton: true,
        cancelButtonText: '取消',
        confirmButtonText: '開啟勿擾模式',
        reverseButtons: true,
        customClass: 'chat-dnd-swal-container',
        onOpen: (modal) => {
          const until = modal.querySelector('[data-role="dnd-until"]');
          const buttons = [...modal.querySelectorAll('.chat-dnd-option')];
          const syncSelected = () => {
            buttons.forEach((button, index) => {
              button.classList.toggle('selected', index === selectedIndex);
            });

            if (until) {
              until.textContent = choices[selectedIndex].notDisturbUntilText;
            }
          };

          buttons.forEach((button) => {
            button.addEventListener('click', () => {
              selectedIndex = Number(button.dataset.index);
              syncSelected();
            });
          });
        },
        preConfirm: () => choices[selectedIndex],
      });

      return result.value || null;
    },

    async showDisableDoNotDisturbSwal() {
      const untilText = this.formatDoNotDisturbDisplay(this.doNotDisturbUntil);
      const result = await this.$swal.fire({
        title: '關閉勿擾',
        html: `
          <div class="chat-dnd-swal">
            <div class="chat-dnd-disable-text">
              您已經開啟勿擾模式<br>
              ${untilText} 前<br>
              不會收到通知
            </div>
          </div>
        `,
        showCancelButton: true,
        cancelButtonText: '取消',
        confirmButtonText: '關閉勿擾模式',
        reverseButtons: true,
        customClass: 'chat-dnd-swal-container',
      });

      return !!result.value;
    },

    updateDoNotDisturbIconAfterToggle(enabled) {
      clearTimeout(this.iconFadeTimers.doNotDisturb);
      this.doNotDisturbIconFading = false;

      if (enabled) {
        this.showDoNotDisturbInactiveIcon = false;
        return;
      }

      this.showDoNotDisturbInactiveIcon = true;
      this.$nextTick(() => {
        this.doNotDisturbIconFading = true;
        this.iconFadeTimers.doNotDisturb = setTimeout(() => {
          this.showDoNotDisturbInactiveIcon = false;
          this.doNotDisturbIconFading = false;
        }, 900);
      });
    },

    updatePinIconAfterToggle(pinned) {
      clearTimeout(this.iconFadeTimers.pin);
      this.pinIconFading = false;

      if (pinned) {
        this.showPinInactiveIcon = false;
        return;
      }

      this.showPinInactiveIcon = true;
      this.$nextTick(() => {
        this.pinIconFading = true;
        this.iconFadeTimers.pin = setTimeout(() => {
          this.showPinInactiveIcon = false;
          this.pinIconFading = false;
        }, 900);
      });
    },

    async handleSetStatus(status) {
      try {
        await this.$store.dispatch('liffChatroom/updateChatroomStatus', {
          chatroomId: this.chatroomId,
          status,
        });
        this.$set(this.chatroom, 'status', status);
      } catch (e) {
        console.error('set status error:', e);
      }
    },

    enterRecallMode() {
      this.showRecallCheckBox = true;
      this.selectedRecallMessages = [];
    },

    cancelRecallMode() {
      this.showRecallCheckBox = false;
      this.selectedRecallMessages = [];
    },

    handleRecallToggle(id, type) {
      const idx = this.selectedRecallMessages.findIndex((rm) => rm.id === id && rm.type === type);
      if (idx === -1) {
        this.selectedRecallMessages.push({ id, type });
      } else {
        this.selectedRecallMessages.splice(idx, 1);
      }
    },

    async handleRecallSubmit() {
      if (!this.selectedRecallMessages.length) return;

      const sender = this.getSelectedRecallSender() || {};
      const employeeCode = this.$store.state.liffChatroom.user.staff.employee_code;
      const senderName = sender.name || '';

      const result = await this.$swal.fire({
        title: '你確定要回收嗎？',
        html: `
          <div>員工編號：${employeeCode}</div>
          <div>姓名：${senderName}</div>
          <br/>
          <div>回收後，相關訊息將無法回復，<br/>請您確認！</div>
        `,
        type: 'warning',
        showCancelButton: true,
        cancelButtonText: '不回收',
        confirmButtonText: '確定回收',
        cancelButtonColor: '#3085d6',
        confirmButtonColor: '#d33',
        reverseButtons: true,
      });

      if (!result.value) return;

      try {
        this.messageRecalling = true;
        await this.$store.dispatch('liffChatroom/recallMessages', {
          chatroomId: this.chatroomId,
          recallMessages: this.selectedRecallMessages,
        });
        this.cancelRecallMode();
      } catch (e) {
        console.error('recall error:', e);
      } finally {
        this.messageRecalling = false;
      }
    },
    async openMultipleUploadPage() {
      let payload = {
        title: this.getChatroomTitle(this.chatroom),
        avatar_url: this.getChatroomAvatar(this.chatroom),
        chatroom_id: this.chatroom.id,
        access_token: await liff.getAccessToken(),
        as_staff: this.$store.state.liffChatroom.asStaff,
      };

      let response = await this.generateMultipleUploadToken({ payload });

      let urlParams = new URLSearchParams(window.location.search);

      let redirectBackUrl = `https://liff.line.me/${this.liffId}/chatrooms/${
        this.chatroom.id
      }?${urlParams.toString()}`;

      console.log(response);
      console.log(window.location);
      console.log(redirectBackUrl);

      let url = `${window.location.origin}/${this.$route.params.orgCode}/liff/chatrooms-multiple-upload?token=${response.token}&redirect_back_url=${redirectBackUrl}&openExternalBrowser=1`;

      liff.openWindow({
        url: url,
        external: true,
      });
    },
    handleMessageRecalled(event) {
      let { message } = event;
      this.messages.map(function(m) {
        if (m.id == message.id) {
          Object.assign(m, message)
          Object.assign(m, { content: "(訊息已收回)" })
        }
      })
      this.$forceUpdate();
    },
    scrollToUnreadMessage() {
      this.$nextTick(() => {
        const chatbox = this.$refs.chatbox
        console.debug('[debug] [scrollToUnreadMessage] firstUnreadMessage', this.firstUnreadMessage)
        if (!this.firstUnreadMessage) return
        const dom = chatbox.querySelector(`#message-${this.firstUnreadMessage.id}`)
        console.debug('[debug] [scrollToUnreadMessage] dom', dom)
        if (!dom) return
        const offset = 60
        const scrollTop = chatbox.scrollTop + dom.getBoundingClientRect().top - offset
        console.debug('[debug] [scrollToUnreadMessage] scrollTop', scrollTop)
        this.$nextTick(() => chatbox.scrollTo(0, scrollTop))
      })
    },
  },
  watch: {
    loading: function () {
      this.$nextTick(function () {
        let chatbox = document.querySelector(".chatbox");
        if (chatbox.scrollHeight != this.scrollHeight) {
          let html = document.querySelector("html");
          html.classList.toggle("unsmooth");
          chatbox.scrollTop = chatbox.scrollHeight - this.scrollHeight;
          this.scrollHeight = chatbox.scrollHeight;
          html.classList.toggle("unsmooth");
        }
      });
    },
    sending: function () {
      this.$nextTick(function () {
        this.scrollToBottom();
        this.focusInput();
      });
    },

    fetching: function () {
      this.$nextTick(function () {
        this.scrollToBottom();
      });
    },
    messages: function () {
      this.$nextTick(function () {
        if (this.bottom) this.scrollToBottom();
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.chatroom {
  .b-avatar {
    width: 35px;
    height: 35px;
  }
  .chatbox {
    position: relative;
    overflow-y: auto;
    height: 100vh; /* Fallback for browsers that do not support Custom Properties */
    height: calc(var(--vh, 1vh) * 100);
    width: 100%;

    .chatbox-inner {
      min-height: 100%;
    }
  }


  .typebox {
    position: sticky;
    bottom: 0;
    width: 100%;
  }

  header {
    background: linear-gradient(90deg, #1e88e5 0%, #2196f3 100%), #1976d2;
    color: #fff;
    min-height: 52px;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .chat-back {
    flex: 0 0 auto;
  }

  .chat-title {
    align-items: center;
    display: flex;
    flex: 0 1 50%;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
    max-width: 50%;
    min-width: 0;
    overflow: hidden;
    text-align: left;
  }

  .chat-title-avatar {
    flex: 0 0 auto;
  }

  .chat-title-text {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chat-title-icons {
    display: inline-flex;
    flex: 0 0 auto;
    gap: 0.25rem;

    .mdi {
      font-size: 1.1rem;
      line-height: 1;
      opacity: 1;
      transition: opacity 0.9s ease;

      &.fading {
        opacity: 0;
      }
    }
  }

  .chat-header-actions {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    justify-content: flex-end;
    margin-left: auto;
    min-width: 0;
  }

  .chat-header-search {
    flex: 1 1 auto;
    justify-content: flex-end;
    min-width: 0;
  }

  .chat-header-search-input {
    max-width: 160px;
    min-width: 0;
  }

  .chat-toolbar-row + .chat-toolbar-row {
    margin-top: 0.25rem;
  }

  .chat-toolbar-row {
    gap: 0.75rem;

    > .btn,
    > .dropdown {
      flex: 1 1 0;
      margin-right: 0 !important;
      min-width: 0;
    }

    > .btn,
    ::v-deep .dropdown-toggle {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }
  }

  .message-box {
    margin-right: auto;
    display: block;
    width: 100%;
    order: 2;

    .message-wrapper {
      max-width: 80%;
      display: inline-block;

      .message {
        font-size: 1.3rem;
        background-color: #f4f6f9;
        border-radius: 1rem;
        border-bottom-left-radius: 0;

        ::v-deep a {
          color: inherit !important;
          text-decoration: underline;
          word-break: break-all;
        }

        &.image {
          padding: 0 !important;
          border-radius: 0.5rem;
          overflow: hidden;
          cursor: pointer;
          display: grid;
          grid-template-columns: minmax(0, 360px);

          div {
            width: 100%;
            height: 0;
            overflow: hidden;
            padding-top: 100%;
            position: relative;

            img {
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              left: 0;
              object-fit: cover;
              width: 100%;
              height: 100%;
              max-width: 100%;
              max-height: 100%;
            }
          }

          &.multiple {
            grid-template-columns: repeat(2, minmax(0, 360px));
            gap: 0.5rem;
            background-color: transparent;

            .expanded {
              padding-top: 50%;
              grid-column: span 2;
            }
          }
        }

        &.file {
          border-radius: 1rem !important;

          p {
            margin: 0;
          }

          a {
            color: inherit;
            background: transparent;
            display: flex;
            align-items: center;
            text-decoration: none !important;

            .icon {
              width: 3rem;
              height: 3rem;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 1rem;
              background-color: rgba(255, 255, 255, 0.25);
              border-radius: 50%;
              flex-shrink: 0;

              svg {
                width: 2rem;
                height: 2rem;
                opacity: 0.75;
              }
            }
          }
        }

        &.sticker {
          background-color: transparent;

          img {
            width: 120px;
          }
        }
      }
    }

    &.owner {
      align-self: flex-end;
      text-align: right;
      margin-left: auto;
      margin-right: 0;

      .message {
        font-size: 1.3rem;
        background-color: #2196f3;
        border-radius: 1rem;
        border-bottom-right-radius: 0;
        color: white;
        text-align: left;
      }
      .draft-message {
        background-color: #ACD6FF;
        border-radius: 1rem;
        border-bottom-right-radius: 0;
        color: white;
        text-align: left;
      }
    }
  }

  .textbox {
    position: relative;
    .btn.file,
    .btn.send {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }
    .btn.file {
      left: 0px;
    }
    .message-box {
      height: calc(45px + 1.25em * (var(--lines) - 1));
      max-height: calc(45px + 1.25em * 9);
      line-height: 1.25;
      padding: calc((45px - 1.25em) / 2) 45px;
      overflow: hidden;
    }
    .btn.send {
      right: 0px;
    }
  }

  .unread {
    left: 0;
    top: -45px;
    width: 100%;
    text-align: center;
    opacity: 0.3;
  }

  .strike {
    display: flex;
    flex-direction: row;

    &:before,
    &:after {
      content: "";
      flex: 1 1;
      border-bottom: 1px solid #ccc;
      margin: auto 5px;
    }
  }

  .fetching {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .read-list {
    margin: 0;
    margin-right: 0.5rem;
    display: flex;
    justify-content: flex-end;

    li + li {
      margin-left: 0.125rem;
    }
  }

  .typing-list {
    order: 1;
  }

  .loading-spinner {
    order: 3;
  }

  .error-message-box {
    height: 100vh;
  }

  small {
    font-size: 90%;
  }

  .last-message-wrapper {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-width: 25rem;
  }

  .download-all {
    color: #999;
    font-size: 0.85em;
  }

  .show-scroll-to-unread-message-btn {
    transform: rotate(180deg);
  }
}
</style>
<style lang="scss">
.dropdown-toggle-no-caret {
  &::after {
    content: none !important;
  }
}

.dropdown-menu{
  &:focus-visible{
    outline:none;
  }
}

.swal2-popup.chat-dnd-swal-container,
.chat-dnd-swal-container .swal2-popup {
  border: 1px solid #666;
  border-radius: 1rem;
  color: #444;
  padding: 1.25rem 1.5rem 1rem;
  width: 22rem;
}

.chat-dnd-swal-container {
  .swal2-title {
    color: #444;
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }

  .swal2-content {
    color: #444;
    font-size: 1.15rem;
  }

  .swal2-actions {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    margin-top: 1.25rem;
  }

  .swal2-styled {
    border-radius: 0.5rem;
    font-size: 1rem;
    padding: 0.45rem 1rem;
  }

  .swal2-cancel {
    background: #fff !important;
    border: 1px solid #ff8f8f !important;
    color: #e96060 !important;
  }

  .swal2-confirm {
    background: #3d7fdb !important;
    color: #fff !important;
  }
}

.chat-dnd-options {
  display: grid;
  gap: 0.75rem;
  margin: 0 auto 1.25rem;
  max-width: 15rem;
}

.chat-dnd-option {
  align-items: center;
  background: #fff;
  border: 1px solid #aaa;
  border-radius: 0.75rem;
  color: #444;
  display: flex;
  font-size: 1.35rem;
  justify-content: center;
  line-height: 1.2;
  padding: 0.55rem 1rem;
  width: 100%;

  &.selected {
    background: #b9dca9;
    border-color: #6aad53;
  }
}

.chat-dnd-option-check {
  font-size: 1.4rem;
  margin-right: 0.75rem;
}

.chat-dnd-until,
.chat-dnd-disable-text {
  line-height: 1.35;
}
</style>
